import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '@/api'
import type { IntegrationDefinition, IntegrationValidationResponse } from '@/api/core/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Activity,
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  FileSearch,
  FileText,
  Gauge,
  Radar,
  ShieldCheck,
  XCircle,
} from 'lucide-react'

const stigRuleDeltaAnalyzerPath = `${import.meta.env.BASE_URL}stig-rule-delta-analyzer/index.html`

const serviceCards = [
  {
    id: 'stig-id-inspector',
    name: 'STIG ID Inspector',
    description: 'Assess systems in your eMASS instance for STIG ID drift impacts',
    icon: Radar,
    href: stigRuleDeltaAnalyzerPath,
    standalone: true,
  },
  {
    id: 'artifact-check',
    name: 'ArtiFact Check',
    description: 'Perfom a full artifact analysis on selected systems in eMASS in support of Authorization decisions',
    icon: FileSearch,
  },
  {
    id: 'ssp-assistant',
    name: 'SSP Assistant',
    description: "Assess a system's SSP or generate an SSP based on your organization's specific requirements.",
    icon: FileText,
  },
]

type ValidationByIntegration = Record<string, IntegrationValidationResponse | Error>

function getReachabilityBadge(validation: IntegrationValidationResponse | Error | undefined) {
  if (!validation) {
    return {
      label: 'Checking',
      variant: 'pending' as const,
      icon: Activity,
    }
  }

  if (validation instanceof Error) {
    return {
      label: 'Unreachable',
      variant: 'destructive' as const,
      icon: XCircle,
    }
  }

  if (validation.validated) {
    return {
      label: 'Reachable',
      variant: 'low' as const,
      icon: CheckCircle2,
    }
  }

  return {
    label: 'Unreachable',
    variant: 'destructive' as const,
    icon: XCircle,
  }
}

function getValidationDetail(validation: IntegrationValidationResponse | Error | undefined) {
  if (!validation) {
    return 'Reachability check in progress'
  }

  if (validation instanceof Error) {
    return validation.message
  }

  if (validation.error) {
    return validation.error
  }

  const status = validation.http_status ? `HTTP ${validation.http_status}` : validation.status
  return validation.validated ? `Validated ${status}` : `Validation returned ${status}`
}

export function EMASSistDashboardPage() {
  const {
    data: integrationList,
    isLoading: integrationsLoading,
    isError: integrationsError,
  } = useQuery({
    queryKey: ['metadata', 'integrations', 'emass'],
    queryFn: () => api.metadata.integrations.list({ integration_type: 'emass' }),
  })

  const emassIntegrations = useMemo(
    () => integrationList?.integrations.filter((integration) => integration.integration_type === 'emass') ?? [],
    [integrationList]
  )

  const { data: validations = {}, isLoading: validationsLoading } = useQuery({
    queryKey: ['metadata', 'integrations', 'emass', 'reachability', emassIntegrations.map(i => i.integration_id)],
    enabled: emassIntegrations.length > 0,
    queryFn: async () => {
      const results = await Promise.all(
        emassIntegrations.map(async (integration) => {
          try {
            const validation = await api.metadata.integrations.validate(integration.integration_id, {
              version: integration.version,
            })
            return [integration.integration_id, validation] as const
          } catch (error) {
            return [
              integration.integration_id,
              error instanceof Error ? error : new Error('Reachability check failed'),
            ] as const
          }
        })
      )

      return Object.fromEntries(results) as ValidationByIntegration
    },
  })

  const reachableCount = emassIntegrations.filter(
    (integration) => {
      const validation = validations[integration.integration_id]
      return !(validation instanceof Error) && validation?.validated
    }
  ).length

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="p-2">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <ShieldCheck className="h-6 w-6 text-emerald-400" />
              </div>
            </div>
            <h1 className="text-2xl font-display font-bold">eMASSist</h1>
            <p className="mt-2 max-w-2xl text-muted-foreground">
              eMASS-focused authorization support for configured SPECTREIQ integrations.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:min-w-[300px]">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Instances</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{emassIntegrations.length}</div>
                <p className="mt-1 text-xs text-muted-foreground">configured in MetadataIQ</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Reachable</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-400">{reachableCount}</div>
                <p className="mt-1 text-xs text-muted-foreground">passing validation</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <div>
        <h2 className="mb-4 text-lg font-semibold">Services</h2>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <Card className="xl:row-span-2">
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div className="p-2 rounded-lg bg-background/50">
                  <Gauge className="h-5 w-5" />
                </div>
                <Badge variant="info">{validationsLoading ? 'Checking' : `${reachableCount}/${emassIntegrations.length}`}</Badge>
              </div>
              <CardTitle className="mt-3 text-base">eMASStatus</CardTitle>
              <CardDescription>
                Current status of eMASS instances configured in SPECTREIQ. To add eMASS instances, please visit the Integrations function under MetadataIQ
              </CardDescription>
            </CardHeader>
            <CardContent>
              {integrationsError && (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                  Unable to load configured eMASS integrations.
                </div>
              )}

              {integrationsLoading && (
                <div className="space-y-3">
                  {[0, 1, 2].map((item) => (
                    <div key={item} className="h-16 rounded-md border border-border bg-muted/30" />
                  ))}
                </div>
              )}

              {!integrationsLoading && !integrationsError && emassIntegrations.length === 0 && (
                <div className="rounded-md border border-border bg-muted/20 p-4">
                  <p className="text-sm font-medium">No eMASS instances configured</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Add eMASS instances in MetadataIQ Integrations to populate this status view.
                  </p>
                  <Button asChild variant="outline" size="sm" className="mt-4">
                    <Link to="/metadataiq/integrations">
                      Integrations
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              )}

              {emassIntegrations.length > 0 && (
                <div className="space-y-3">
                  {emassIntegrations.map((integration: IntegrationDefinition) => {
                    const validation = validations[integration.integration_id]
                    const reachability = getReachabilityBadge(validation)
                    const ReachabilityIcon = reachability.icon

                    return (
                      <div
                        key={`${integration.integration_id}-${integration.version}`}
                        className="rounded-md border border-border p-3"
                      >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">{integration.display_name}</p>
                            <p className="mt-1 break-all text-xs text-muted-foreground">{integration.base_url}</p>
                            <p className="mt-2 text-xs text-muted-foreground">
                              {integration.integration_id} • v{integration.version}
                              {integration.provider ? ` • ${integration.provider}` : ''}
                            </p>
                          </div>
                          <Badge variant={reachability.variant} className="w-fit gap-1">
                            <ReachabilityIcon className="h-3 w-3" />
                            {reachability.label}
                          </Badge>
                        </div>
                        <p className="mt-3 text-xs text-muted-foreground">
                          {getValidationDetail(validation)}
                        </p>
                      </div>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {serviceCards.map((service) => {
            const ServiceIcon = service.icon

            return (
              <Card key={service.id} className="transition-colors hover:border-primary/50">
                <CardHeader>
                  <div className="p-2 rounded-lg bg-background/50 w-fit">
                    <ServiceIcon className="h-5 w-5" />
                  </div>
                  <CardTitle className="mt-3 text-base">{service.name}</CardTitle>
                  <CardDescription>{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  {service.href ? (
                    <Button asChild variant="outline" className="w-full justify-between">
                      {service.standalone ? (
                        <a href={service.href}>
                          <span>Open Service</span>
                          <ClipboardCheck className="h-4 w-4" />
                        </a>
                      ) : (
                        <Link to={service.href}>
                          <span>Open Service</span>
                          <ClipboardCheck className="h-4 w-4" />
                        </Link>
                      )}
                    </Button>
                  ) : (
                    <Button variant="outline" className="w-full justify-between" disabled>
                      <span>Open Service</span>
                      <ClipboardCheck className="h-4 w-4" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>
    </div>
  )
}
